import React, { useState, useEffect } from 'react';
import { Button, LazyDayPicker, Modal } from 'common/lazy';
import { useClassName } from 'common/hooks';
import { DateTime } from 'luxon';
import './operational-hours-modal.less';
import {
  DesktopOnly,
  getContentfulField,
  MobileOnly,
  SVGIcon,
} from 'common/components';
import {
  CharacterAppearances,
  TemporaryClosures,
  useCharacterAppearances,
  getClosuresForDate,
} from 'public/components/DateSchedule';
import { ControlledTabs } from 'public/components/Tabs';
import { formatOpeningHoursForDate } from 'utils/helpers/openingHours';

const OperationalHoursModal = ({
  open,
  trigger,
  onOpen,
  onClose,
  initialDate,
  isDateUnavailable,
  updateSelectedDate,
  openingHoursBanner,
  openingHours,
  ridesCards = [],
  venueSlug,
  venueId,
  emptyAsset,
  emptyAssetStyle,
  // Optional: when provided, the modal shows a tab bar and lets the user
  // switch between several entities (e.g. the attractions of a bundle),
  // each with its own hours. When omitted, behavior is unchanged (single
  // entity, driven by the props above).
  tabs,
}) => {
  const classNames = useClassName('OperationalHoursModal');
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const hasTabs = Array.isArray(tabs) && tabs.length > 0;
  const activeTab = hasTabs ? tabs[activeTabIndex] || tabs[0] : null;

  const effectiveOpeningHours = hasTabs ? activeTab.openingHours : openingHours;
  const effectiveOpeningHoursBanner = hasTabs
    ? activeTab.openingHoursBanner
    : openingHoursBanner;
  const effectiveVenueSlug = hasTabs ? activeTab.venueSlug : venueSlug;
  const effectiveVenueId = hasTabs ? activeTab.venueId : venueId;
  const effectiveRidesCards = hasTabs ? activeTab.ridesCards || [] : ridesCards;
  const effectiveIsDateUnavailable = hasTabs
    ? activeTab.isDateUnavailable || (() => false)
    : isDateUnavailable;
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(() => {
    if (initialDate) {
      try {
        return DateTime.fromISO(initialDate).toJSDate();
      } catch (e) {
        return new Date();
      }
    }
    return new Date();
  });
  const toISO = (jsDate) => {
    if (!jsDate || !(jsDate instanceof Date)) return null;
    return DateTime.fromJSDate(jsDate).toISODate();
  };

  useEffect(() => {
    if (initialDate) {
      try {
        const newDate = DateTime.fromISO(initialDate).toJSDate();
        setCalendarSelectedDate(newDate);
      } catch (e) {
        console.error('Error parsing initialDate:', e);
      }
    }
  }, [initialDate]);

  const characterAppearances = useCharacterAppearances(
    effectiveVenueId,
    toISO(calendarSelectedDate)
  );

  const isDateBeforeToday = (date) => {
    if (!date || !(date instanceof Date)) return false;
    return new Date(date.toDateString()) < new Date(new Date().toDateString());
  };

  const onDayClick = (date) => {
    if (!date || !(date instanceof Date)) return;
    if (isDateBeforeToday(date)) return;

    const isoDate = toISO(date);
    if (isoDate && effectiveIsDateUnavailable(isoDate)) return;

    setCalendarSelectedDate(date);
    updateSelectedDate(date);
  };

  const formatSelectedDateAsShort = (date) => {
    if (!date || !(date instanceof Date)) return '';

    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };

    if (!date || !(date instanceof Date)) return '';
    const dt = DateTime.fromJSDate(date);
    const day = dt.day;
    const suffix = getOrdinalSuffix(day);
    return `${dt.toFormat('LLL d')}${suffix}`;
  };

  const formatSelectedDate = (date) => {
    if (!date || !(date instanceof Date)) return '';
    return DateTime.fromJSDate(date).toFormat('cccc, LLLL d, yyyy');
  };

  const closedInfo = getClosuresForDate(
    effectiveRidesCards,
    calendarSelectedDate
  );

  const useEmptyImage = !(
    characterAppearances?.length > 0 ||
    closedInfo ||
    effectiveOpeningHoursBanner
  );

  return (
    <Modal
      basic
      open={open}
      trigger={trigger}
      onOpen={onOpen}
      size="small"
      onClose={onClose}
      className={classNames('modal')}>
      <div className={classNames('container')}>
        <div className={classNames('inner')}>
          <h3 className={classNames('container__title')}>
            <span>Operational Hours</span>
            <button
              className={classNames('container__title__button')}
              onClick={onClose}>
              <SVGIcon
                name="close"
                className={classNames('container__title__button__icon')}
              />
            </button>
          </h3>
          {hasTabs && tabs.length > 1 && (
            <ControlledTabs
              names={tabs.map((tab) => tab.label)}
              selectedIndex={activeTabIndex}
              styles={{ container: classNames('container__tabs') }}
              onClick={(name) =>
                setActiveTabIndex(tabs.findIndex((tab) => tab.label === name))
              }
            />
          )}
          <div className={classNames('container__content')}>
            {useEmptyImage && emptyAsset && (
              <img
                style={emptyAssetStyle}
                src={getContentfulField(emptyAsset.fields.file).url}
                alt={getContentfulField(emptyAsset.fields.file).title}
                className={classNames('container__content__hours__no-content')}
              />
            )}
            <div className={classNames('container__content__calendar')}>
              <LazyDayPicker
                className={`day-picker`}
                disabledDays={[
                  { before: DateTime.local().toJSDate() },
                  (jsDate) => {
                    if (!jsDate || !(jsDate instanceof Date)) return false;
                    const iso = toISO(jsDate);
                    return iso ? effectiveIsDateUnavailable(iso) : false;
                  },
                ]}
                enableOutsideDaysClick={false}
                selectedDays={calendarSelectedDate}
                month={calendarSelectedDate || new Date()}
                onDayClick={(date) => onDayClick(date)}
              />
              {effectiveVenueSlug && (
                <DesktopOnly style={{ marginTop: 'auto' }}>
                  <Button
                    onClick={() => {
                      window.location.href = `/venue/${effectiveVenueSlug}/tickets/${DateTime.fromJSDate(
                        calendarSelectedDate
                      ).toFormat('yyyy-MM-dd')}`;
                    }}
                    fluid
                    inverted
                    size="large"
                    className={classNames(
                      'container__content__calendar__button'
                    )}>
                    Get Tickets For{' '}
                    {formatSelectedDateAsShort(calendarSelectedDate)}
                  </Button>
                </DesktopOnly>
              )}
            </div>

            <div className={classNames('container__content__hours')}>
              <p className={classNames('container__content__hours__date')}>
                {formatSelectedDate(calendarSelectedDate)}
              </p>
              <p className={classNames('container__content__hours__time')}>
                {formatOpeningHoursForDate(
                  effectiveOpeningHours,
                  calendarSelectedDate
                )}
              </p>
              {effectiveOpeningHoursBanner &&
                effectiveOpeningHoursBanner?.asset && (
                  <img
                    src={effectiveOpeningHoursBanner.asset.mobile.assetUrl}
                    alt={effectiveOpeningHoursBanner.asset.name}
                    className={classNames('container__content__hours__banner')}
                  />
                )}
              <CharacterAppearances appearances={characterAppearances} />
              <TemporaryClosures closedInfo={closedInfo} />
            </div>
          </div>
        </div>
        {effectiveVenueSlug && (
          <MobileOnly>
            <Button
              onClick={() => {
                window.location.href = `/venue/${effectiveVenueSlug}/tickets/${DateTime.fromJSDate(
                  calendarSelectedDate
                ).toFormat('yyyy-MM-dd')}`;
              }}
              fluid
              inverted
              size="large"
              className={classNames('container__content__calendar__button')}>
              Get Tickets For {formatSelectedDateAsShort(calendarSelectedDate)}
            </Button>
          </MobileOnly>
        )}
      </div>
    </Modal>
  );
};

export default OperationalHoursModal;
